import dayjs from "dayjs";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";

import VisitStatusChip from "./VisitStatusChip";
import getCustomNames from "../../../../utils/customNames";
import { ECRF_STATUSES } from "../../../../utils/constants";

const EVENT_ICONS = {
  "-3": FactCheckOutlinedIcon,
  "-1": ReportProblemOutlinedIcon,
  "-2": EventRepeatOutlinedIcon,
  "-4": DescriptionOutlinedIcon,
  "-6": MenuBookOutlinedIcon,
};

const formatDate = (date) => (date ? dayjs(date).format("DD MMM YYYY") : null);

const visitSubtitle = (visit, overview) => {
  if (visit.visit_number === -1) return `${visit.special_adverse_count ?? 0} reported`;
  if (visit.visit_number === -2) return `${visit.special_unscheduled_count ?? 0} recorded`;
  if (visit.visit_number === -4) return "Additional eCRF forms";
  if (visit.visit_number === -6) return "Diary entries";
  if (!overview) return "Not scheduled yet";

  const open = formatDate(overview.visit_start_date);
  const close = formatDate(overview.visit_completed_date);
  if (open && close) return `${open} – ${close}`;
  return open ? `Opens ${open}` : "Window not set";
};

const VisitAvatar = ({ visitNumber, expanded }) => {
  const Icon = EVENT_ICONS[visitNumber];
  const isAdverse = visitNumber === -1;

  return (
    <Avatar
      variant="rounded"
      sx={(theme) => {
        const tone = isAdverse ? theme.palette.error.main : theme.palette.primary.main;
        return {
          width: 40,
          height: 40,
          borderRadius: 2,
          fontSize: "0.85rem",
          fontWeight: 700,
          color: tone,
          bgcolor: alpha(tone, expanded ? 0.16 : 0.08),
          transition: theme.transitions.create("background-color"),
        };
      }}
    >
      {Icon ? <Icon fontSize="small" /> : visitNumber + 1}
    </Avatar>
  );
};

const VisitCard = ({ visit, overview, expanded, onToggle, children }) => {
  const showInClinic = overview?.has_inclinic_activity;
  const showInApp = overview?.has_inapp_activity;

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        borderRadius: 3,
        overflow: "hidden",
        borderColor: expanded ? alpha(theme.palette.primary.main, 0.5) : "divider",
        boxShadow: expanded ? `0 6px 20px ${alpha(theme.palette.common.black, 0.06)}` : "none",
        transition: theme.transitions.create(["border-color", "box-shadow"]),
      })}
    >
      <ButtonBase
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={`visit-card-${visit.visit_number}`}
        sx={{
          width: "100%",
          textAlign: "left",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: 2,
          rowGap: 1,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ flex: "1 1 220px", minWidth: 0 }}
        >
          <VisitAvatar visitNumber={visit.visit_number} expanded={expanded} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
              {visit.visit_name || getCustomNames(visit.visit_number)}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {visitSubtitle(visit, overview)}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ flex: "0 1 auto" }}
        >
          {showInClinic && (
            <VisitStatusChip label="In-clinic" status={overview.inclinic_visit_status} />
          )}
          {showInApp && <VisitStatusChip label="App" status={overview.inapp_visit_status} />}
          {overview?.ecrf_status === ECRF_STATUSES.INCOMPLETE && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label="eCRF issues"
              sx={{ fontWeight: 600, borderRadius: 1.5 }}
            />
          )}
          <ExpandMoreIcon
            sx={(theme) => ({
              color: "text.secondary",
              transform: expanded ? "rotate(180deg)" : "none",
              transition: theme.transitions.create("transform"),
            })}
          />
        </Stack>
      </ButtonBase>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider />
        <Box
          sx={{
            p: { xs: 1.5, sm: 3 },
            bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.02),
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default VisitCard;
